import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import KlaroLogo from '@/components/haccp/KlaroLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const initialVersion = new URLSearchParams(window.location.search).get('plan_version_id') || '';
  const [form, setForm] = useState({ company_name: '', admin_name: '', admin_email: '', plan_version_id: initialVersion, vat_number: '', address: '', phone: '' });
  const [plans, setPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { fetch(`${BACKEND_URL}/api/public/plans`).then(r => r.ok ? r.json() : Promise.reject()).then(rows => { setPlans(rows); setForm(f => ({ ...f, plan_version_id: rows.some(p => p.plan_version_id === f.plan_version_id) ? f.plan_version_id : (rows[0]?.plan_version_id || '') })); }).catch(() => toast.error('Piani temporaneamente non disponibili')); }, []);

  const submit = async () => {
    if (!form.company_name || !form.admin_name || !form.admin_email || !form.plan_version_id) return toast.error('Nome azienda, nome amministratore ed email sono obbligatori');
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/registration/company-requests`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Errore invio richiesta');
      setSubmitted(true);
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="surface max-w-md w-full p-8 text-center">
          <KlaroLogo />
          <div className="mx-auto mt-6 w-14 h-14 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg></div>
          <h1 className="mt-4 font-heading text-2xl font-semibold">Controlla la tua email</h1>
          <p className="mt-2 text-sm text-[#475467]">Abbiamo inviato un link di verifica a <strong>{form.admin_email}</strong>. Il link scade tra 24 ore.</p>
          <div className="mt-6 text-left rounded-xl bg-slate-50 border border-slate-200 p-4">
            <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Prossimi passaggi</p>
            <ol className="space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><span className="font-semibold text-blue-600">1.</span> Verifica la tua email cliccando il link ricevuto</li>
              <li className="flex gap-2"><span className="font-semibold text-blue-600">2.</span> Klaro esamina la richiesta e la approva</li>
              <li className="flex gap-2"><span className="font-semibold text-blue-600">3.</span> Riceverai una <strong>seconda email</strong> con il link per creare la password e accedere</li>
            </ol>
          </div>
          <Button data-testid="register-back-btn" variant="outline" className="mt-6" onClick={() => navigate('/login')}>Torna al login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="surface max-w-xl w-full p-6 sm:p-8">
        <KlaroLogo />
        <h1 className="mt-6 font-heading text-2xl font-semibold">Registra la tua azienda</h1>
        <p className="mt-1 text-sm text-[#475467]">Verrai contattato via email per verificare l'indirizzo. L'attivazione richiede l'approvazione dell'amministratore Klaro.</p>
        <div className="mt-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nome azienda *</Label><Input data-testid="register-company-name" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} /></div>
            <div><Label>Piano</Label>
              <Select value={form.plan_version_id} onValueChange={v => setForm({ ...form, plan_version_id: v })}>
                <SelectTrigger data-testid="register-plan"><SelectValue placeholder={plans.length ? 'Seleziona' : 'Piani non disponibili'} /></SelectTrigger>
                <SelectContent>{plans.map(p => <SelectItem key={p.plan_version_id} value={p.plan_version_id}>{p.name}{p.limits?.users != null ? ` · ${p.limits.users} utenti` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nome amministratore *</Label><Input data-testid="register-admin-name" value={form.admin_name} onChange={e => setForm({ ...form, admin_name: e.target.value })} /></div>
            <div><Label>Email amministratore *</Label><Input data-testid="register-admin-email" type="email" value={form.admin_email} onChange={e => setForm({ ...form, admin_email: e.target.value })} /></div>
            <div><Label>P.IVA</Label><Input data-testid="register-vat" value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value })} /></div>
            <div><Label>Telefono</Label><Input data-testid="register-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Indirizzo</Label><Input data-testid="register-address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
          </div>
          <Button data-testid="register-submit-btn" variant="success" className="w-full" onClick={submit} disabled={submitting || !plans.length}>{submitting ? 'Invio…' : 'Invia richiesta'}</Button>
          <p className="text-center text-sm text-[#667085]">Hai già un account? <Link className="text-[#2563EB] font-semibold" to="/login">Accedi</Link></p>
        </div>
      </div>
    </div>
  );
}
